import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { isPopupOpen } from '../../../shared/utils/popupUtils';

export function useTaskEditing(tasks) {
  const [editingTaskId, setEditingTaskId] = useState(null); // Now stores clientId (_reactKey)
  const [editingDueDateTaskId, setEditingDueDateTaskId] = useState(null); // Now stores clientId (_reactKey)
  const editingElementRef = useRef(null);
  const dateInputRef = useRef(null);
  const caretAtZeroTaskIdsRef = useRef(new Set()); // Now stores clientIds (_reactKey)

  // Set initial content when editing starts and focus the element
  const prevEditingTaskIdRef = useRef(null);
  
  useLayoutEffect(() => {
    if (editingTaskId !== null && (prevEditingTaskIdRef.current === null || prevEditingTaskIdRef.current !== editingTaskId)) {
      // editingTaskId changed - initial setup
      
      // Don't auto-focus if a popup is open (e.g. login) so user can focus the popup input
      if (isPopupOpen()) {
        prevEditingTaskIdRef.current = editingTaskId;
        return;
      }
      
      const element = document.querySelector(`.task-text[data-react-key="${editingTaskId}"]`);
      
      if (element) {
        const task = tasks.find(t => t._reactKey === editingTaskId);
        if (task && (!element.textContent || element.textContent.trim() === '')) {
          element.textContent = task.text || '';
        }
        editingElementRef.current = element;
        
        // Check if caret is already positioned (e.g., by handleTaskTextClick or arrow keys)
        const selection = window.getSelection();
        let caretAlreadyPositioned = false;
        if (selection.rangeCount > 0) {
          const existingRange = selection.getRangeAt(0);
          if (element.contains(existingRange.startContainer)) {
            // Caret is already in this element, check if it's not at the end
            const preCaretRange = existingRange.cloneRange();
            preCaretRange.selectNodeContents(element);
            preCaretRange.setEnd(existingRange.endContainer, existingRange.endOffset);
            const currentPos = preCaretRange.toString().length;
            const textLength = element.textContent.length;
            // If caret is not at the end (or start if shouldCaretBeAtZero), assume it was positioned by click or arrow key
            const shouldCaretBeAtZero = caretAtZeroTaskIdsRef.current.has(editingTaskId);
            if (!shouldCaretBeAtZero && currentPos < textLength) {
              caretAlreadyPositioned = true;
            } else if (shouldCaretBeAtZero && currentPos !== 0) {
              // Should be at zero but isn't - will position it
            }
          }
        }
        
        if (!caretAlreadyPositioned) {
          // Focus the element and position cursor
          element.focus();
          const range = document.createRange();
          
          // Check if this task should have cursor at position zero
          const shouldCaretBeAtZero = caretAtZeroTaskIdsRef.current.has(editingTaskId);
          
          if (shouldCaretBeAtZero) {
            // Position cursor at the start
            range.selectNodeContents(element);
            range.collapse(true);
            // Keep the flag until editing ends
          } else {
            // Position cursor at the end (default behavior)
            range.selectNodeContents(element);
            range.collapse(false);
          }
          
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // Just focus, don't reposition
          element.focus();
        }
      }
    }
    prevEditingTaskIdRef.current = editingTaskId;
  }, [editingTaskId, tasks]);
  
  
  // Clear the caret position flag when editing ends
  useEffect(() => {
    if (editingTaskId === null && prevEditingTaskIdRef.current !== null) {
      // Editing ended, clear any remaining flags
      caretAtZeroTaskIdsRef.current.clear();
    }
  }, [editingTaskId]);

  // Callback ref to open calendar popup when date input is rendered (queueMicrotask to run after commit)
  const dateInputCallbackRef = (inputElement) => {
    dateInputRef.current = inputElement;
    if (inputElement && editingDueDateTaskId !== null) {
      queueMicrotask(() => {
        if (typeof inputElement.showPicker === 'function') {
          try {
            inputElement.showPicker();
          } catch (error) {
            inputElement.focus();
          }
        } else {
          inputElement.focus();
        }
      });
    }
  };

  return {
    editingTaskId,
    setEditingTaskId,
    editingDueDateTaskId,
    setEditingDueDateTaskId,
    editingElementRef,
    dateInputRef,
    dateInputCallbackRef,
    caretAtZeroTaskIdsRef
  };
}

